/**
 * Timing and artwork for the manual flow's transient card guide (VEX-7717) —
 * the front/back hint painted over Regula's live camera view.
 *
 * The guide itself is CSS: it rides on Regula's own `::part(hint-container)`,
 * which Regula sizes and positions onto the document-frame rectangle on every
 * layout pass, so it tracks resizes, letterboxing and Regula's button cluster
 * for free. CaptureScreen only decides *when* it exists; the stylesheet does
 * the rest.
 */
/** How long the guide stays on screen. MUST match the `ssnCardGuideFade`
 * animation duration in SsnCardVerification.module.scss. */
export const CSS_GUIDE_DURATION_MS = 2000;

/**
 * How long to wait after the camera starts before the guide is allowed to
 * exist at all.
 *
 * This delay lives in JS rather than as a lead-in in the CSS keyframes because
 * of the back guide's animated SVG: a `::before` starts its background image's
 * internal animation the moment the pseudo-element exists, and `opacity: 0`
 * does NOT pause it. Fading in from a dead lead-in therefore revealed the flip
 * already part-way through — the front face is only visible for the first 25%
 * of that SVG's 2s loop. Withholding `data-guide-side` keeps the rule from
 * matching, so no pseudo-element and no SVG clock exist until reveal time.
 */
export const CSS_GUIDE_REVEAL_DELAY_MS = 150;

const GUIDE_SRC = {
  front: "/regula-assets/ssnCard/ssn_card_front.svg",
  // The auto flow's own flip re-skin still points at ssn_card_back.svg; this
  // animated one is manual-only.
  back: "/regula-assets/ssnCard/ssn_flip_animation.svg",
} as const;

/**
 * URL for a side's guide artwork, made unique per capture.
 *
 * The `?v=` is not cache-busting for its own sake — it is what makes the back
 * guide's flip animation start from the beginning on a retry. A browser keys an
 * animated SVG's timeline to the *image resource*, not to the element showing
 * it: every element pointing at the same URL shares one continuously-running
 * timeline. So the second time we show the flip SVG, it renders wherever that
 * shared clock happens to be rather than at frame 0 — and since the back face
 * fills 75% of the 2s loop, that usually means the card appears already
 * flipped, then flips again when the loop wraps. A distinct URL is a distinct
 * resource, and therefore a fresh timeline.
 *
 * Pass a value from {@link nextGuideNonce} — never anything that can repeat
 * within a page load, or the timeline is shared again and the bug returns.
 */
export function guideAssetUrl(side: "front" | "back", nonce: number): string {
  return `${GUIDE_SRC[side]}?v=${nonce}`;
}

/** Monotonic across the whole page load — see {@link guideAssetUrl}. */
let guideRun = 0;

/**
 * A nonce that is unique for the life of the page.
 *
 * Deliberately not derived from anything scoped to a component (a capture key,
 * an index): those restart at zero if their owner ever remounts, which would
 * hand two different guides the same URL — and therefore the same shared,
 * mid-loop animation timeline. A module-level counter cannot collide no matter
 * how the flow above it is restructured.
 */
export function nextGuideNonce(): number {
  guideRun += 1;
  return guideRun;
}
